import { useEffect } from "react";
import { DescriptionListItem } from "@design-system/DescriptionList/DescriptionList";
import taskStyles from "@components/drawers/TaskDrawerFields.module.scss";
import {
    FinancialAccount,
    FinancialAccountType,
} from "@common/types/financialAccount";
import { FINANCIAL_TYPES_AVAILABLE_FOR_RECONCILIATION } from "@common/helpers/reconciliation";
import { useScope } from "@state/scopeContext.lib";
import { FinancialAccountCombobox } from "@components/financialAccount/AccountsFilter/FinancialAccountCombobox";
import { MultiSelectFilterCombobox } from "./MultiSelectFilterCombobox";
import {
    readReconciliationFinancialAccountIds,
    readReconciliationFinancialAccountTypes,
    serializeReconciliationCloseTemplateViewConfig,
} from "./closeTemplateTaskViewConfig";

interface Props {
    viewConfig: Record<string, unknown> | null;
    onViewConfigChange: React.Dispatch<
        React.SetStateAction<Record<string, unknown> | null>
    >;
}

const FINANCIAL_ACCOUNT_TYPE_LABEL: Record<FinancialAccountType, string> = {
    [FinancialAccountType.CHECKING]: "Checking",
    [FinancialAccountType.SAVINGS]: "Savings",
    [FinancialAccountType.CREDIT_CARD]: "Credit card",
    [FinancialAccountType.LOAN]: "Loan",
    [FinancialAccountType.INVESTMENT]: "Investment",
    [FinancialAccountType.PAYMENT_PROCESSOR]: "Payment processor",
    [FinancialAccountType.CASH]: "Cash",
    [FinancialAccountType.LINE_OF_CREDIT]: "Line of credit",
    [FinancialAccountType.OTHER]: "Other",
};

// Only offer account types that reconciliation actually supports — matches
// FINANCIAL_TYPES_AVAILABLE_FOR_RECONCILIATION, the same list the backend
// enforces when running a reconciliation.
const RECONCILIATION_ELIGIBLE_ACCOUNT_TYPES =
    FINANCIAL_TYPES_AVAILABLE_FOR_RECONCILIATION;

// Restrict the pinned-account picker to the same eligible types the
// reconciliation table and backend execution accept. Without this an operator
// could pin a loan/investment/cash/etc account that the reconciliation table
// silently drops and execution rejects, producing a task with no account to
// reconcile.
const isReconciliationEligibleAccount = (account: FinancialAccount): boolean =>
    RECONCILIATION_ELIGIBLE_ACCOUNT_TYPES.includes(account.financialType);

export const ReconciliationFields = ({
    viewConfig,
    onViewConfigChange,
}: Props) => {
    // A workspaceId in scope means this template task belongs to a
    // client-specific (workspace-scoped) close template — only those can pin
    // exact accounts, since account IDs are workspace-specific. Org-wide
    // templates keep the generic "any account of these types" behavior.
    const { workspaceId } = useScope();
    const isClientSpecific = Boolean(workspaceId);

    const storedTypes = readReconciliationFinancialAccountTypes(viewConfig);
    const accountIds = readReconciliationFinancialAccountIds(viewConfig);
    const hasPinnedAccounts = accountIds.length > 0;
    // Existing templates may have stored a type that is no longer
    // reconciliation-eligible (e.g. cash, loan). Drop those so they aren't
    // counted in the trigger label or left un-deselectable, and persist the
    // normalized set so the template stops targeting a type reconciliation
    // execution rejects.
    const selectedTypes = storedTypes.filter((type) =>
        RECONCILIATION_ELIGIBLE_ACCOUNT_TYPES.includes(type),
    );
    const hasIneligibleStoredTypes =
        selectedTypes.length !== storedTypes.length;

    useEffect(() => {
        if (hasIneligibleStoredTypes && !hasPinnedAccounts) {
            onViewConfigChange(
                serializeReconciliationCloseTemplateViewConfig(selectedTypes),
            );
        }
    }, [
        hasIneligibleStoredTypes,
        hasPinnedAccounts,
        selectedTypes,
        onViewConfigChange,
    ]);

    return (
        <>
            {isClientSpecific && (
                <DescriptionListItem label="Financial accounts" alignTop>
                    <div className={taskStyles.detailSelectWrapper}>
                        <FinancialAccountCombobox
                            multiple
                            value={accountIds}
                            accountFilterPredicate={
                                isReconciliationEligibleAccount
                            }
                            onValueChange={(nextAccountIds) => {
                                onViewConfigChange(
                                    serializeReconciliationCloseTemplateViewConfig(
                                        // Selecting specific accounts overrides
                                        // the account-type filter — keep only
                                        // one targeting mode active at a time.
                                        nextAccountIds.length > 0
                                            ? []
                                            : selectedTypes,
                                        nextAccountIds,
                                    ),
                                );
                            }}
                            buttonSize="md"
                        />
                    </div>
                </DescriptionListItem>
            )}
            <DescriptionListItem label="Financial account types" alignTop>
                <div className={taskStyles.detailSelectWrapper}>
                    <MultiSelectFilterCombobox<FinancialAccountType>
                        items={RECONCILIATION_ELIGIBLE_ACCOUNT_TYPES}
                        selectedValues={selectedTypes}
                        onValuesChange={(next) => {
                            onViewConfigChange(
                                serializeReconciliationCloseTemplateViewConfig(
                                    next,
                                    // Choosing types clears any pinned
                                    // accounts — same mutual-exclusivity.
                                    [],
                                ),
                            );
                        }}
                        getLabel={(type) => FINANCIAL_ACCOUNT_TYPE_LABEL[type]}
                        placeholder={
                            hasPinnedAccounts
                                ? "Using specific accounts above"
                                : "All account types"
                        }
                        searchPlaceholder="Search account types..."
                        emptyMessage="No account types found"
                        showClearButton={false}
                        triggerButtonProps={{
                            className: taskStyles.detailSelectTrigger,
                            block: false,
                            disabled: isClientSpecific && hasPinnedAccounts,
                        }}
                    />
                </div>
            </DescriptionListItem>
        </>
    );
};
